// ===========================================
// User API Functions
// ===========================================
// API calls for user account management (deletion, export)

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export interface DeletionBlocker {
  type: 'OUTSTANDING_BALANCE' | 'PENDING_BOOKINGS' | 'ACTIVE_LESSONS' | 'ONLY_ADMIN';
  message: string;
  details?: Record<string, unknown>;
}

export interface DeletionBlockersResponse {
  status: 'success';
  data: {
    canDelete: boolean;
    blockers: DeletionBlocker[];
  };
}

export interface DeletionSummary {
  user: { email: string; name: string };
  dataToDelete: {
    students: number;
    enrollments: number;
    attendanceRecords: number;
    notes: number;
    resources: number;
  };
  dataToAnonymize: {
    invoices: number;
    payments: number;
  };
  gracePeriodDays: number;
}

export interface DeletionSummaryResponse {
  status: 'success';
  data: DeletionSummary;
}

export interface RequestDeletionRequest {
  password: string;
  reason?: string;
}

export interface RequestDeletionResponse {
  status: 'success';
  message: string;
  data: {
    gracePeriodEnds: string;
  };
}

export interface CancelDeletionResponse {
  status: 'success';
  message: string;
}

export interface DeleteChildRequest {
  password: string;
}

export interface DeleteChildResponse {
  status: 'success';
  message: string;
}

// ===========================================
// PARENT SELF-SERVICE TYPES
// ===========================================

export interface FamilyMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  isPrimary: boolean;
  relationship: string;
}

export interface StudentSummary {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  email: string | null;
  phone: string | null;
  avatar: string | null;
  ageGroup: string;
  enrollmentCount: number;
  attendanceRate: number | null;
  isActive: boolean;
  notes: string | null;
  familyId: string;
}

export interface FamilyWithMembers {
  id: string;
  name: string;
  parents: FamilyMember[];
  students: StudentSummary[];
}

export interface ParentProfile {
  id: string;
  userId: string;
  isPrimary: boolean;
  contactDetailsLocked: boolean;
  user: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
  };
  contact1: {
    name: string;
    email: string;
    phone: string;
    relationship: string;
  };
  contact2: {
    name: string | null;
    email: string | null;
    phone: string | null;
    relationship: string | null;
  } | null;
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  contact1Name?: string;
  contact1Email?: string;
  contact1Phone?: string;
  contact1Relationship?: string;
  contact2Name?: string | null;
  contact2Email?: string | null;
  contact2Phone?: string | null;
  contact2Relationship?: string | null;
  emergencyName?: string;
  emergencyPhone?: string;
  emergencyRelationship?: string;
}

export interface FamilyResponse {
  status: 'success';
  data: FamilyWithMembers;
}

export interface ChildrenResponse {
  status: 'success';
  data: StudentSummary[];
}

export interface ProfileResponse {
  status: 'success';
  data: ParentProfile;
}

// ===========================================
// API FUNCTIONS
// ===========================================

/**
 * Get any blockers preventing account deletion
 */
export async function getDeletionBlockers(): Promise<DeletionBlockersResponse> {
  return apiClient.get<DeletionBlockersResponse>('/users/me/deletion-blockers');
}

/**
 * Get a summary of what data will be deleted
 */
export async function getDeletionSummary(): Promise<DeletionSummaryResponse> {
  return apiClient.get<DeletionSummaryResponse>('/users/me/deletion-summary');
}

/**
 * Request account deletion
 */
export async function requestAccountDeletion(
  data: RequestDeletionRequest
): Promise<RequestDeletionResponse> {
  return apiClient.post<RequestDeletionResponse>('/users/me/request-deletion', data);
}

/**
 * Cancel a pending deletion request
 */
export async function cancelAccountDeletion(): Promise<CancelDeletionResponse> {
  return apiClient.post<CancelDeletionResponse>('/users/me/cancel-deletion', {});
}

/**
 * Export user data (GDPR)
 */
export async function exportUserData(): Promise<{ blob: Blob; schoolName: string }> {
  // Backend returns data directly (not wrapped), and apiClient.get extracts res.data
  const data = await apiClient.get<Record<string, unknown> & { schoolName?: string }>('/users/me/export');
  const schoolName = (data.schoolName as string) || 'school';
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  return { blob, schoolName };
}

/**
 * Delete a child account (parent only)
 */
export async function deleteChildAccount(
  studentId: string,
  data: DeleteChildRequest
): Promise<DeleteChildResponse> {
  return apiClient.delete<DeleteChildResponse>(`/users/me/children/${studentId}`, { data });
}

// ===========================================
// PARENT SELF-SERVICE API FUNCTIONS
// ===========================================

/**
 * Get parent's family information
 */
export async function getMyFamily(): Promise<FamilyResponse> {
  return apiClient.get<FamilyResponse>('/users/me/family');
}

/**
 * Get parent's children (students)
 */
export async function getMyChildren(): Promise<ChildrenResponse> {
  return apiClient.get<ChildrenResponse>('/users/me/children');
}

/**
 * Get rich enrollments for one of the parent's children (includes classmates, colors, etc.)
 */
export async function getMyChildEnrollments(studentId: string): Promise<{ status: 'success'; data: StudentEnrollment[] }> {
  return apiClient.get(`/users/me/children/${studentId}/enrollments`);
}

/**
 * Update additional info (notes) for one of the parent's children. The server takes the
 * family from the session (#455).
 */
export async function updateStudentNotes(
  studentId: string,
  notes: string | null
): Promise<{ status: 'success'; data: { id: string; firstName: string; lastName: string; notes: string | null } }> {
  return apiClient.patch(`/users/me/children/${studentId}/notes`, { notes });
}

/**
 * Get parent's own profile
 */
export async function getMyProfile(): Promise<ProfileResponse> {
  return apiClient.get<ProfileResponse>('/users/me/profile');
}

/**
 * Update parent's own profile
 */
export async function updateMyProfile(
  data: UpdateProfileRequest
): Promise<ProfileResponse> {
  return apiClient.patch<ProfileResponse>('/users/me/profile', data);
}

// ===========================================
// STUDENT SELF-SERVICE TYPES
// ===========================================

export interface StudentDashboardProfile {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  avatar: string | null;
  ageGroup: { id: string; name: string; minAge: number | null; maxAge: number | null } | null;
  family: { id: string; name: string } | null;
  enrollmentCount: number;
  attendanceRate: number | null;
}

export interface Classmate {
  id: string;
  firstName: string;
  avatar: string | null;
  ageGroup: string | null;
}

export interface StudentEnrollment {
  id: string;
  lessonId: string;
  enrolledAt: string;
  lesson: {
    id: string;
    name: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    instrument: string | null;
    instrumentColor: string | null;
    lessonType: string | null;
    lessonTypeColor: string | null;
    programLevel: string | null;
    programLevelColor: string | null;
    teacher: string | null;
    teacherColor: string | null;
    location: string | null;
    room: string | null;
    classmates: Classmate[];
  };
}

export interface StudentAttendanceRecord {
  id: string;
  date: string;
  status: string;
  notes: string | null;
  lesson: { id: string; name: string };
}

export interface StudentProfileResponse {
  status: 'success';
  data: StudentDashboardProfile;
}

export interface StudentEnrollmentsResponse {
  status: 'success';
  data: StudentEnrollment[];
}

export interface StudentAttendanceResponse {
  status: 'success';
  data: StudentAttendanceRecord[];
}

// ===========================================
// STUDENT SELF-SERVICE API FUNCTIONS
// ===========================================

/**
 * Get student's own profile (for student dashboard)
 */
export async function getMyStudentProfile(): Promise<StudentProfileResponse> {
  return apiClient.get<StudentProfileResponse>('/users/me/student-profile');
}

/**
 * Update student's avatar
 */
export async function updateMyStudentAvatar(
  avatar: string | null
): Promise<StudentProfileResponse> {
  return apiClient.patch<StudentProfileResponse>('/users/me/student-avatar', { avatar });
}

/**
 * Update child's avatar (for parents)
 */
export async function updateChildAvatar(
  studentId: string,
  avatar: string | null
): Promise<{ status: 'success'; data: { id: string; avatar: string | null } }> {
  return apiClient.patch(`/users/me/children/${studentId}/avatar`, { avatar });
}

/**
 * Update child's contact details (for parents)
 */
export interface UpdateStudentContactInput {
  email?: string | null;
  phone?: string | null;
}

export async function updateChildContact(
  studentId: string,
  data: UpdateStudentContactInput
): Promise<{ status: 'success'; data: { id: string; email: string | null; phone: string | null } }> {
  return apiClient.patch(`/users/me/children/${studentId}/contact`, data);
}

/**
 * Set a child's date of birth (for parents). `birthDate` is `YYYY-MM-DD`.
 * The office is told by email when it changes.
 */
export interface ChildBirthDateResult {
  id: string;
  birthDate: string;
  ageGroup: { id: string; name: string } | null;
  changed: boolean;
}

export async function updateChildBirthDate(
  studentId: string,
  birthDate: string
): Promise<{ status: 'success'; data: ChildBirthDateResult }> {
  return apiClient.patch(`/users/me/children/${studentId}/birth-date`, { birthDate });
}

/**
 * Get student's own enrollments/classes
 */
export async function getMyStudentEnrollments(): Promise<StudentEnrollmentsResponse> {
  return apiClient.get<StudentEnrollmentsResponse>('/users/me/student-enrollments');
}

/**
 * Get student's own attendance history
 */
export async function getMyStudentAttendance(params?: {
  limit?: number;
  startDate?: string;
  endDate?: string;
}): Promise<StudentAttendanceResponse> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);

  const query = queryParams.toString();
  return apiClient.get<StudentAttendanceResponse>(
    `/users/me/student-attendance${query ? `?${query}` : ''}`
  );
}

/**
 * Get notes shared with the student
 */
export async function getMyStudentNotes(params?: {
  lessonId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{ status: 'success'; data: unknown[] }> {
  const queryParams = new URLSearchParams();
  if (params?.lessonId) queryParams.append('lessonId', params.lessonId);
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);

  const query = queryParams.toString();
  return apiClient.get(`/users/me/student-notes${query ? `?${query}` : ''}`);
}

/**
 * Get resources shared with the student
 */
export async function getMyStudentResources(params?: {
  lessonId?: string;
}): Promise<{ status: 'success'; data: unknown[] }> {
  const queryParams = new URLSearchParams();
  if (params?.lessonId) queryParams.append('lessonId', params.lessonId);

  const query = queryParams.toString();
  return apiClient.get(`/users/me/student-resources${query ? `?${query}` : ''}`);
}
